/**
 * Calmy support page — the four topic cards, the help centre and the one form.
 *
 * WHAT THIS FILE DOES NOT DO: it never submits, cancels, returns or changes
 * anything. A card is a link to the form on the same page; choosing one sets
 * a <select> and scrolls. The only thing that ever leaves the browser is the
 * customer's own Shopify contact POST, fired by the Send button, and this
 * file does not call submit() anywhere.
 *
 * WITHOUT JAVASCRIPT the page still works: each card is an <a href="#…">
 * pointing at the form, the form is a plain Shopify contact form with its own
 * labels and required attributes, the topic is an ordinary <select>, and the
 * section's <noscript> block unhides every help article at once so the answers
 * are readable rather than locked behind a swap this file would have done.
 *
 * WHY sessionStorage. Shopify's `form` object echoes back only name, email and
 * body after a failed post — the topic and order number are custom fields and
 * come back empty. So every field is mirrored into sessionStorage as it is
 * typed and restored on the next render, which also covers a customer who
 * navigates away and returns. It is cleared the moment a submission succeeds.
 *
 * THE HELP CENTRE NEVER RELOADS THE PAGE, which is the whole reason it swaps
 * views instead of linking out: the form keeps its DOM, and with it every
 * character already typed into it.
 *
 * THE FORM IS A VIEW OF THIS PAGE, not a second page and not a second form.
 * Five controls open it — the four action cards, the Get support card, and
 * every "Still need help?" button in the help centre — and all five call the
 * same goToForm(). Back restores the overview exactly as it was left, down to
 * the scroll position and the help article that was open.
 */
(function () {
  "use strict";

  var KEY = "calmy:support:" + window.location.pathname;
  var FIELDS = ["name", "email", "topic", "order", "body"];

  function slice(list) { return Array.prototype.slice.call(list); }

  function store(data) {
    try { sessionStorage.setItem(KEY, JSON.stringify(data)); } catch (e) {}
  }
  function read() {
    try { return JSON.parse(sessionStorage.getItem(KEY) || "{}") || {}; } catch (e) { return {}; }
  }
  function forget() {
    try { sessionStorage.removeItem(KEY); } catch (e) {}
  }

  function init(root) {
    if (!root || root.__calmySupport) return;
    root.__calmySupport = true;

    var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var target = root.querySelector("[data-calmy-support-target]");
    var heading = root.querySelector("[data-calmy-support-heading]");
    var cards = slice(root.querySelectorAll("[data-calmy-topic]"));
    var form = root.querySelector("form.csup__form");

    /* ── The two views ───────────────────────────────────────────────────
       Which one is showing is decided in Liquid and only changed here, so a
       page that came back from a submission carrying an error or a success
       is already on the form at first paint and never blinks through the
       overview. */
    var overview = root.querySelector('[data-support-view="overview"]');
    var formView = root.querySelector('[data-support-view="form"]');
    var returnY = null;

    function showOverview() {
      if (!overview || !formView) return;
      formView.hidden = true;
      overview.hidden = false;
      /* Back to where they were reading, not to the top of the page. */
      if (returnY !== null) {
        window.scrollTo({ top: returnY, behavior: reduce ? "auto" : "smooth" });
        returnY = null;
      } else if (root.scrollIntoView) {
        root.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
      }
    }

    function showForm() {
      if (!overview || !formView) return false;
      if (formView.hidden) returnY = window.scrollY;
      overview.hidden = true;
      formView.hidden = false;
      return true;
    }

    /* A successful post is the one state that clears the draft, and the only
       one where there are no fields to wire — the help centre below is wired
       either way. */
    var posted = !!root.querySelector("[data-calmy-support-success]");
    if (posted) forget();

    var els = {};
    if (form && !posted) {
      FIELDS.forEach(function (k) { els[k] = form.querySelector('[data-calmy-f="' + k + '"]'); });
    }

    function save() {
      if (!form || posted) return;
      var data = {};
      FIELDS.forEach(function (k) { if (els[k]) data[k] = els[k].value; });
      store(data);
    }

    /* ── Which card is lit ────────────────────────────────────────────────
       Driven by the <select>, never by the click, so it is right whether the
       topic was chosen from a card, from a help article, from the dropdown,
       or restored from a failed submission. */
    function paint() {
      var value = els.topic ? els.topic.value : "";
      cards.forEach(function (c) {
        var on = c.getAttribute("data-calmy-topic") === value;
        c.classList.toggle("is-selected", on);
        if (on) c.setAttribute("aria-current", "true");
        else c.removeAttribute("aria-current");
      });
    }

    /* The one route to the form used by the cards and by every "Still need
       help?" button. It selects a topic and scrolls; it never submits.

       The offset under the sticky header is scroll-margin-top in the
       stylesheet, so the browser does the arithmetic with the header's real
       measured height rather than a number guessed here.

       Focus lands on the form's heading, not on a field: moving it into an
       input would open the keyboard on a phone over the form the customer has
       just been shown. preventScroll, because the smooth scroll is already on
       its way and focus would otherwise snap it to the end. */
    function goToForm(topic) {
      if (topic && els.topic) {
        var has = Array.prototype.some.call(els.topic.options, function (o) { return o.value === topic; });
        if (has) {
          els.topic.value = topic;
          paint();
          save();
        }
      }
      /* Shown FIRST: a hidden element has no box, so scrolling to it before
         it is displayed scrolls to nothing. Passing no topic leaves the
         select exactly as it stands — the fresh-visit default, or whatever
         the customer chose earlier. */
      showForm();
      if (target) target.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
      if (heading) {
        window.setTimeout(function () {
          try { heading.focus({ preventScroll: true }); } catch (err) { heading.focus(); }
        }, reduce ? 0 : 420);
      }
    }

    /* The Get support card and the Back control. Delegated, so the controls
       inside a view that is currently hidden are covered by the same handler. */
    root.addEventListener("click", function (e) {
      var opener = e.target.closest ? e.target.closest("[data-support-open-form]") : null;
      if (opener && root.contains(opener)) {
        e.preventDefault();
        goToForm(null);
        return;
      }
      var back = e.target.closest ? e.target.closest("[data-support-back]") : null;
      if (back && root.contains(back)) {
        e.preventDefault();
        showOverview();
      }
    });

    /* A link straight to the form — the anchor Shopify itself sends the page
       back to after a submission, and the href the action cards carry when
       this file has not loaded yet. */
    var hash = window.location.hash;
    if (hash === "#ccontact-form" || hash === "#ccontact-form-send") {
      showForm();
      if (target) target.scrollIntoView({ block: "start" });
    }

    if (form && !posted) {
      /* ── Restore ────────────────────────────────────────────────────────
         Only into a field the server left empty, so Shopify's own echo of
         name, email and body after a failed post always wins. */
      var saved = read();
      FIELDS.forEach(function (k) {
        var el = els[k];
        if (!el || !saved[k]) return;
        if (el.tagName === "SELECT") {
          var has = Array.prototype.some.call(el.options, function (o) { return o.value === saved[k]; });
          if (has) el.value = saved[k];
        } else if (!el.value) {
          el.value = saved[k];
        }
      });

      FIELDS.forEach(function (k) {
        if (!els[k]) return;
        els[k].addEventListener("input", save);
        els[k].addEventListener("change", save);
      });

      if (els.topic) els.topic.addEventListener("change", paint);
      paint();

      cards.forEach(function (card) {
        card.addEventListener("click", function (e) {
          if (!els.topic) return; /* no form on the page: let the href do its job */
          e.preventDefault();
          goToForm(card.getAttribute("data-calmy-topic"));
        });
      });

      wireValidation(form, els);
      wireSubmitGuard(form);
    }

    wireHelp(root, goToForm, reduce);
  }

  /* ═══ Validation ═════════════════════════════════════════════════════════
     The browser's own constraints are left in place — required and type=email
     still work with this file absent or broken. What is added is the wording
     and the placement: the native bubble is suppressed and the same message is
     written under the field it belongs to, where it stays visible and is
     announced. */
  function wireValidation(form, els) {
    function messageFor(el) {
      if (el.validity.valueMissing) {
        return el.getAttribute("data-calmy-msg-missing") || "This field is required.";
      }
      if (el.validity.typeMismatch) {
        return el.getAttribute("data-calmy-msg-type") || "Check the format of this field.";
      }
      return el.validationMessage || "Check this field.";
    }
    function slotFor(el) {
      var field = el.closest(".csup__field");
      return field ? field.querySelector("[data-calmy-ferr]") : null;
    }
    FIELDS.forEach(function (k) {
      var el = els[k];
      if (!el) return;
      el.addEventListener("input", function () {
        if (!el.checkValidity()) return;
        el.removeAttribute("aria-invalid");
        var slot = slotFor(el);
        if (slot) slot.textContent = "";
      });
      el.addEventListener("invalid", function (e) {
        e.preventDefault();
        el.setAttribute("aria-invalid", "true");
        var slot = slotFor(el);
        if (slot) slot.textContent = messageFor(el);
        var first = form.querySelector('[aria-invalid="true"]');
        if (first === el) {
          try { el.focus({ preventScroll: false }); } catch (err) { el.focus(); }
        }
      });
    });
  }

  /* ═══ One submission at a time ═══════════════════════════════════════════
     `submit` only fires once the browser's constraints pass, so a form stopped
     by validation is never locked. The flag is what blocks a second press; the
     button is disabled a tick later, after the browser has built the request
     from the form it was attached to. */
  function wireSubmitGuard(form) {
    var sending = false;
    form.addEventListener("submit", function (e) {
      if (sending) { e.preventDefault(); return; }
      if (form.querySelector(":invalid")) return; /* the browser stops it and fires `invalid` */
      sending = true;
      form.classList.add("is-sending");
      form.setAttribute("aria-busy", "true");
      var btn = form.querySelector("[data-calmy-support-submit]");
      if (btn) window.setTimeout(function () { btn.disabled = true; }, 0);
    });

    /* Back/forward cache can restore this page with the button still disabled
       and the flag still set. */
    window.addEventListener("pageshow", function (ev) {
      if (!ev.persisted) return;
      sending = false;
      form.classList.remove("is-sending");
      form.removeAttribute("aria-busy");
      var btn = form.querySelector("[data-calmy-support-submit]");
      if (btn) btn.disabled = false;
    });
  }

  /* ═══ The help centre ════════════════════════════════════════════════════
     Every view is already in the page. This shows one at a time, keeps the
     address bar in step so Back works, and searches the articles it can see.
     It makes no network request of any kind — in particular it never touches
     the store's product search. */
  function wireHelp(root, goToForm, reduce) {
    var help = root.querySelector("[data-calmy-help]");
    if (!help) return;

    var views = slice(help.querySelectorAll(".chelp__view"));
    if (!views.length) return;

    var home = help.querySelector('[data-help-view="home"]');
    var searchView = help.querySelector('[data-help-view="search"]');
    var resultsList = help.querySelector("[data-help-results]");
    var resultsH = help.querySelector("[data-help-results-h]");
    var noneBox = help.querySelector("[data-help-none]");
    var crumbQ = help.querySelector("[data-help-crumb-q]");
    var input = help.querySelector("[data-calmy-help-q]");

    var one = help.getAttribute("data-label-article") || "article";
    var many = help.getAttribute("data-label-articles") || "articles";

    var byId = {};
    views.forEach(function (v) { if (v.id) byId[v.id] = v; });

    /* The search index is read from the articles themselves, so an article
       edited in the theme editor is searchable the moment it renders. */
    var index = slice(help.querySelectorAll('[data-help-view="art"]')).map(function (v) {
      var q = v.querySelector(".chelp__ah");
      var a = v.querySelector(".chelp__answer");
      var question = q ? q.textContent.trim() : "";
      var answer = a ? a.textContent.replace(/\s+/g, " ").trim() : "";
      return {
        id: v.id,
        question: question,
        answer: answer,
        cat: v.getAttribute("data-help-catname") || "",
        /* The category name is part of the haystack: someone searching
           "refund" means the Returns & Refunds articles, not only the one
           article whose answer happens to use the word. */
        hay: (question + " " + answer + " " + (v.getAttribute("data-help-catname") || "")).toLowerCase()
      };
    });

    function viewFor(id) {
      if (!id || id === "home") return home;
      return byId[id] || home;
    }

    function show(id, opts) {
      var el = viewFor(id);
      if (!el) return;
      views.forEach(function (v) { v.hidden = v !== el; });

      if (opts && opts.scroll) {
        help.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
      }
      if (opts && opts.focus) {
        var h = el.querySelector(".chelp__ch, .chelp__ah");
        if (h) {
          window.setTimeout(function () {
            try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); }
          }, reduce ? 0 : 380);
        }
      }
    }

    function hashFor(id) { return id === "home" ? "#help" : "#" + id; }

    function go(id) {
      show(id, { scroll: true, focus: true });
      var h = hashFor(id);
      if (window.history && window.history.pushState && window.location.hash !== h) {
        /* pushState rather than location.hash: setting the hash makes the
           browser hunt for the element itself, and these ids belong to views
           that were hidden a moment ago. */
        window.history.pushState({ calmyHelp: id }, "", h);
      }
    }

    /* Any card, breadcrumb link or back button. One listener on the container
       rather than one per control, so the search results — built later — are
       covered by the same code. */
    help.addEventListener("click", function (e) {
      var opener = e.target.closest ? e.target.closest("[data-help-open]") : null;
      if (opener && help.contains(opener)) {
        e.preventDefault();
        go(opener.getAttribute("data-help-open"));
        return;
      }
      var contact = e.target.closest ? e.target.closest("[data-help-contact]") : null;
      if (contact && help.contains(contact)) {
        e.preventDefault();
        goToForm(contact.getAttribute("data-help-contact"));
      }
    });

    /* ── Search ─────────────────────────────────────────────────────────── */
    function excerpt(text) {
      if (text.length <= 120) return text;
      return text.slice(0, 117).replace(/\s+\S*$/, "") + "…";
    }

    function runSearch(term) {
      if (!resultsList || !searchView) return;
      var t = term.trim().toLowerCase();
      var hits = t.length < 2 ? [] : index.filter(function (a) { return a.hay.indexOf(t) !== -1; });

      /* A question match is what the customer usually means, so those come
         first; the rest keep their authored order. */
      hits.sort(function (a, b) {
        var qa = a.question.toLowerCase().indexOf(t) !== -1 ? 0 : 1;
        var qb = b.question.toLowerCase().indexOf(t) !== -1 ? 0 : 1;
        return qa - qb;
      });

      resultsList.innerHTML = "";
      hits.forEach(function (a) {
        var li = document.createElement("li");
        var link = document.createElement("a");
        link.className = "chelp__art";
        link.href = "#" + a.id;
        link.setAttribute("data-help-open", a.id);

        var cat = document.createElement("span");
        cat.className = "chelp__acat";
        cat.textContent = a.cat;
        var q = document.createElement("span");
        q.className = "chelp__aq";
        q.textContent = a.question;
        var x = document.createElement("span");
        x.className = "chelp__ax";
        x.textContent = excerpt(a.answer);

        link.appendChild(cat);
        link.appendChild(q);
        link.appendChild(x);
        li.appendChild(link);
        resultsList.appendChild(li);
      });

      /* With nothing found the empty-state box says so; a "0 articles"
         heading above it would only say it twice. */
      if (resultsH) resultsH.textContent = hits.length ? hits.length + " " + (hits.length === 1 ? one : many) : "";
      if (noneBox) noneBox.hidden = hits.length !== 0;
      if (crumbQ) crumbQ.textContent = "“" + term.trim() + "”";
    }

    if (input) {
      var timer = null;
      var inSearch = false;
      input.addEventListener("input", function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          var term = input.value.trim();
          if (term.length < 2) {
            if (inSearch) {
              inSearch = false;
              show("home", {});
              if (window.history && window.history.replaceState) {
                window.history.replaceState({ calmyHelp: "home" }, "", "#help");
              }
            }
            return;
          }
          runSearch(term);
          if (!inSearch) {
            inSearch = true;
            /* replaceState, not push: typing should not fill the Back button
               with one entry per keystroke. */
            show(searchView.id || "home", {});
            if (window.history && window.history.replaceState) {
              window.history.replaceState({ calmyHelp: "search" }, "", "#help-search");
            }
          } else {
            show(searchView.id || "home", {});
          }
        }, 140);
      });
      /* Enter in a search field would otherwise try to submit whatever form
         it is inside; this one is inside none, but a browser's "search" reset
         still deserves handling. */
      input.addEventListener("search", function () {
        if (!input.value.trim()) { show("home", {}); }
      });
    }

    /* ── Address bar ────────────────────────────────────────────────────── */
    function fromHash() {
      var h = window.location.hash;
      if (!h || h.indexOf("#help") !== 0) return null;
      if (h === "#help") return "home";
      return h.slice(1);
    }

    window.addEventListener("popstate", function () {
      var id = fromHash();
      show(id || "home", { focus: false });
    });

    var initial = fromHash();
    if (initial) show(initial, { scroll: true, focus: false });
    else show("home", {});

    /* THE THEME EDITOR selects a block by scrolling to it, which cannot work
       while the article it belongs to is one of the hidden views. Opening that
       view first is what makes editing an answer possible at all. */
    document.addEventListener("shopify:block:select", function (e) {
      var view = e.target && e.target.closest ? e.target.closest('[data-help-view="art"]') : null;
      if (view && help.contains(view)) show(view.id, { scroll: true, focus: false });
    });
  }

  function boot(scope) {
    (scope || document).querySelectorAll("[data-calmy-support]").forEach(init);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { boot(); });
  } else {
    boot();
  }
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
})();
